import { memo, useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/ReduxHooks";
import { setCatalogSearchValue } from "../../store/slices/PagePaginagionSlice";
import { useLocation } from "react-router-dom";
import { EMPTY_STRING, SEARCH_PARAMS } from "../../constants/consts";
import { CatalogControl } from "../catalog/catalogControl/CatalogControl";
import { FilterItemsType } from "../catalog/types";
import { useTranslation } from "react-i18next";
import { UIButton, UIInput, SearchOutlined } from "ui";
import "./SearchPanelBase.sass";

interface SearchPanelBaseProps {
  initialSearchValue?: string;
  searchPageParams?: FilterItemsType;
  className?: string;
  showConfigButton?: boolean;
}

export const SearchPanelBase = memo((props: SearchPanelBaseProps) => {
  const location = useLocation();
  const { searchPageParams, initialSearchValue, className = "", showConfigButton } = props;

  const [inputValue, setInputValue] = useState<string>(
    initialSearchValue || EMPTY_STRING
  );
  const [placeholder, setPlaceholder] = useState("");
  const { activeFilters, searchValue } = useAppSelector((state) => state.page);
  const dispatch = useAppDispatch();
  const inputValueRef = useRef(inputValue);
  const { t, i18n } = useTranslation();

  const isConfigVisible = showConfigButton ?? false;
  const searchParams = useMemo(
    () => searchPageParams || SEARCH_PARAMS,
    [searchPageParams]
  );

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    dispatch(setCatalogSearchValue(inputValueRef.current));
  }, [dispatch, location.pathname]);

  useEffect(() => {
    setPlaceholder(activeFilters.map((item) => t(searchParams[item])).join(" / "));
  }, [activeFilters, i18n.language, searchParams, t]);

  useEffect(() => {
    if (typeof searchValue !== "string") return;
    if (searchValue === inputValueRef.current) return;
    setInputValue(searchValue);
  }, [searchValue]);

  const handleOnChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleOnClickSearch = () => {
    dispatch(setCatalogSearchValue(inputValue));
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    dispatch(setCatalogSearchValue(inputValue));
  };

  return (
    <div className={`searchPanelBaseAnt ${className}`.trim()}>
      <UIButton
        type="button"
        variant="outlined"
        className="searchPanelBaseAnt__searchBtn"
        onClick={handleOnClickSearch}
        icon={<SearchOutlined />}
        aria-label="Search catalog"
      />

      <UIInput
        noWrapper
        value={inputValue}
        onChange={handleOnChangeSearch}
        onKeyDown={handleKeyPress}
        type="text"
        placeholder={placeholder}
        className="searchPanelBaseAnt__input"
        style={{ width: "100%" }}
      />

      {isConfigVisible && (
        <CatalogControl
          isSetDisplay={false}
          isDownload={false}
          isSetConfig
          className="searchPanelBaseAnt__config"
          btnClassName="searchPanelBaseAnt__configBtn"
        />
      )}
    </div>
  );
});
